import React, { useEffect, useState } from 'react';
import { Star, CheckCircle2, Phone, ExternalLink, Loader2 } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import '../sales.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/reviews`;
const LOGO = `${process.env.PUBLIC_URL || ''}/sales-logo.png`;
const LABELS = { 1: 'Not happy', 2: 'Could be better', 3: 'It was okay', 4: 'Very good', 5: 'Loved it!' };

const Stars = ({ value, hover, onHover, onPick, size = 'h-12 w-12 sm:h-14 sm:w-14', disabled }) => (
  <div className="flex items-center justify-center gap-1.5 sm:gap-2" data-testid="review-stars" onMouseLeave={() => onHover?.(0)}>
    {[1, 2, 3, 4, 5].map((n) => {
      const on = n <= (hover || value);
      return (
        <button key={n} type="button" disabled={disabled} onMouseEnter={() => onHover?.(n)} onClick={() => onPick?.(n)} aria-label={`${n} star${n > 1 ? 's' : ''}`}
          className={`transition-transform duration-150 ${disabled ? '' : 'hover:scale-110 active:scale-95'}`} data-testid={`review-star-${n}`} data-on={on}>
          <Star className={`${size} drop-shadow-sm ${on ? 'fill-amber-400 text-amber-500' : 'fill-white/60 text-slate-300'}`} strokeWidth={1.5} />
        </button>
      );
    })}
  </div>
);

const Done = ({ d, rating, onEdit }) => (
  <div className="text-center space-y-5 sales-rise" data-testid="review-thanks">
    <div className="mx-auto h-16 w-16 rounded-full bg-teal-600 text-white flex items-center justify-center shadow-lg"><CheckCircle2 className="h-9 w-9" /></div>
    <div>
      <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">Thank you, {d.first_name}!</h1>
      <p className="text-slate-600 mt-2">Your {rating}-star rating has been received.</p>
    </div>
    <Stars value={rating} disabled size="h-9 w-9" />
    {rating >= 4 && d.google_review_url ? (
      <div className="rounded-3xl border border-amber-200 bg-amber-50/80 p-5 sm:p-6 space-y-3" data-testid="review-google-box">
        <p className="font-bold text-slate-900 text-lg">Would you share it with others?</p>
        <p className="text-sm text-slate-700">A quick Google review helps our small, family-run team more than you know — it only takes a minute.</p>
        <a href={d.google_review_url} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-slate-900 text-white font-bold shadow-md hover:bg-slate-800 transition-colors" data-testid="review-google-btn">
          Review us on Google <ExternalLink className="h-4 w-4" />
        </a>
      </div>
    ) : rating <= 3 ? (
      <div className="rounded-3xl border border-slate-200 bg-white/80 p-5 sm:p-6 space-y-2 text-sm text-slate-700" data-testid="review-sorry-box">
        <p className="font-bold text-slate-900 text-base">We're sorry we fell short.</p>
        <p>Your feedback goes straight to our owner. Someone from our team will reach out to make it right.</p>
        {d.company?.phone && <a href={`tel:${d.company.phone}`} className="inline-flex items-center gap-2 font-semibold text-teal-800"><Phone className="h-4 w-4" /> {d.company.phone}</a>}
      </div>
    ) : null}
    <button type="button" onClick={onEdit} className="text-xs text-slate-500 underline underline-offset-2" data-testid="review-edit">Change my rating</button>
  </div>
);

export default function CustomerReviewPage({ token }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [rating, setRating] = useState(() => Math.min(5, Math.max(0, Number(new URLSearchParams(window.location.search).get('stars')) || 0)));
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.documentElement.classList.add('sales-theme');
    document.title = 'How did we do? · PM Home Remodeling';
    fetch(`${API}/${token}`).then(async (r) => {
      if (!r.ok) throw new Error('nf');
      const data = await r.json();
      setD(data);
      if (data.rating) { setDone(true); setRating(data.rating); setComment(data.comment || ''); }
    }).catch(() => setErr(true));
    return () => document.documentElement.classList.remove('sales-theme');
  }, [token]);

  const submit = async () => {
    if (!rating) { toast.error('Please tap a star first'); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/${token}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ rating, comment }) });
      if (!r.ok) throw new Error('fail');
      setD(await r.json()); setDone(true); window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) { toast.error('Something went wrong. Please try again.'); } finally { setBusy(false); }
  };

  if (err) return <div className="min-h-[100dvh] flex items-center justify-center p-6 text-center" data-testid="review-not-found"><div><p className="text-xl font-bold text-slate-900">This link is not valid</p><p className="text-sm text-slate-600 mt-1">Please use the link from your email or contact us.</p></div></div>;
  if (!d) return <div className="min-h-[100dvh] flex items-center justify-center" data-testid="review-loading"><div className="h-12 w-12 rounded-full bg-teal-600/80 animate-pulse shadow-lg" /></div>;

  return (
    <div className="min-h-[100dvh] py-6 sm:py-10" data-testid="review-page">
      <div className="max-w-xl mx-auto px-4 sm:px-6 space-y-6">
        <header className="flex items-center gap-3" data-testid="review-header">
          <img src={LOGO} alt="PM Home Remodeling" className="h-12 w-auto object-contain drop-shadow-sm" />
          <div><p className="font-extrabold text-slate-900 leading-tight">PM Home Remodeling</p><p className="text-[11px] text-slate-500">Windows & Doors · Georgia, USA</p></div>
        </header>

        <section className="rounded-3xl border border-white/80 bg-white/75 backdrop-blur-xl shadow-lg p-6 sm:p-8">
          {done ? <Done d={d} rating={rating} onEdit={() => setDone(false)} /> : (
            <div className="space-y-6 sales-rise">
              <div className="text-center">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-teal-700">Your installation is complete</p>
                <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mt-2 tracking-tight" data-testid="review-title">How did we do, {d.first_name}?</h1>
                <p className="text-sm text-slate-600 mt-2">Tap a star to rate your experience with our team.</p>
              </div>
              <Stars value={rating} hover={hover} onHover={setHover} onPick={setRating} />
              <p className="text-center text-sm font-semibold text-amber-700 h-5" data-testid="review-rating-label">{LABELS[hover || rating] || ''}</p>
              <div>
                <label className="text-xs font-semibold text-slate-600" htmlFor="review-comment">Anything you'd like to tell us? <span className="font-normal text-slate-400">(optional)</span></label>
                <textarea id="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} rows={4} maxLength={2000} placeholder="The crew, the result, anything we could do better…"
                  className="mt-1 w-full rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500/40" data-testid="review-comment" />
              </div>
              <button type="button" onClick={submit} disabled={busy || !rating} className="w-full h-12 rounded-full bg-teal-600 text-white font-bold text-base shadow-md hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center justify-center gap-2" data-testid="review-submit">
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Star className="h-5 w-5 fill-white" />} Send my rating
              </button>
            </div>
          )}
        </section>

        <section className="rounded-3xl border border-white/80 bg-white/60 backdrop-blur-xl p-5 text-sm text-slate-700 space-y-1" data-testid="review-project">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Your project</p>
          <p className="font-bold text-slate-900">{d.customer_name} · {d.contract_number}</p>
          {d.address && <p>{d.address}</p>}
          <p>{d.windows} window{d.windows === 1 ? '' : 's'} · {d.doors} door{d.doors === 1 ? '' : 's'}{d.installed_at ? ` · installed ${new Date(`${d.installed_at}T12:00:00`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}` : ''}</p>
          {d.company?.phone && <p className="pt-1">Questions? Call us at <a href={`tel:${d.company.phone}`} className="font-semibold text-teal-800">{d.company.phone}</a></p>}
        </section>
        <footer className="text-center text-[11px] text-slate-500">© {new Date().getFullYear()} PM Home Remodeling · Georgia, USA</footer>
      </div>
      <Toaster richColors position="top-center" closeButton duration={3500} />
    </div>
  );
}
